import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
} from "@mui/material";
import Footer from "../../component/footer";
import Intro from "../../component/Intro";
import axios from "axios";
import Slider from "react-slick";
import "../CMD/ProjectManage/slider-custom.css";
import loadingGif from "../CMD/loading.gif";
import ImageModal from "./ImageModal";
import parse from "html-react-parser";
import DOMPurify from "dompurify";
import { timeAgo } from "../../component/post-comp/postcomp";

const PostDetail = ({ mode, setmymode }) => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState("");

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(
          `https://sirius.runasp.net/api/Post/${id}`
        );
        setPost(response.data);
      } catch (error) {
        console.error("Error fetching post details", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [id]);

  const handleImageClick = (imageSrc) => {
    setModalImage(imageSrc);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setModalImage("");
  };

  if (loading) {
    return (
      <div
        style={{
          margin: "50px auto",
          textAlign: "center",
          width: "200px",
          height: "200px",
        }}
      >
        <img width={"100%"} src={loadingGif} alt="Loading..." />
      </div>
    );
  }

  if (!post) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        bgcolor="background.paper"
      >
        <Typography variant="h6">Post not found</Typography>
      </Box>
    );
  }

  // Sanitize HTML content with allowed classes and styles
  const cleanHtml = DOMPurify.sanitize(post.content, {
    ALLOWED_ATTR: ['class', 'style'],
    ALLOWED_TAGS: [
      'p', 'ul', 'ol', 'li', 'strong', 'em', 'br', 
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'div'
    ],
  });

  const span = (
    <span style={{ 
      color: "#fff", 
      fontSize: '2rem', 
      display: 'block', 
      wordWrap: 'break-word', 
      lineHeight: '1.2',
      whiteSpace: 'normal',
      overflowWrap: 'break-word',
    }}>
      {post.title}
    </span>
  );

  const textinline = {
    headh1: "",
    spano: span,
  };

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    appendDots: (dots) => (
      <div style={{ padding: "10px" }}>
        <ul style={{ margin: "0px", padding: "0px" }}>{dots}</ul>
      </div>
    ),
    customPaging: (i) => (
      <div
        className="custom-dot"
        style={{
          width: "10px",
          height: "10px",
          background: "lightcoral",
          borderRadius: "50%",
          display: "inline-block",
          margin: "0 5px",
        }}
      />
    ),
    arrows: true,
  };

  return (
    <div>
      <Intro mode={mode} setmymode={setmymode} textinline={textinline} />
      <Box
        component="section"
        py={6}
        padding={"2rem"}
        bgcolor="background.paper"
      >
        <Container>
          <Card
            style={{
              border: "1px solid",
              borderColor: mode === "dark" ? "lightcoral" : "silver",
            }}
          >
            {post.photos && post.photos.length > 1 ? (
              <Slider {...settings}>
                {post.photos.map((photo, index) => (
                  <div key={index} style={{ width: "100%" }}>
                    <CardMedia
                      component="img"
                      height="500"
                      image={`https://sirius.runasp.net/${photo.filePath}`}
                      alt={`post ${post.id} ${index}`}
                      className="focused-image"
                      style={{
                        width: "100%",
                        objectFit: "cover",
                        borderRadius: "10px",
                        outline: "none",
                        border: "none",
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        handleImageClick(
                          `https://sirius.runasp.net/${photo.filePath}`
                        )
                      }
                    />
                  </div>
                ))}
              </Slider>
            ) : (
              post.photos &&
              post.photos.length === 1 && (
                <CardMedia
                  component="img"
                  height="500"
                  image={`https://sirius.runasp.net/${post.photos[0].filePath}`}
                  alt={`post ${post.id}`}
                  className="focused-image"
                  style={{
                    width: "100%",
                    objectFit: "cover",
                    borderRadius: "10px",
                    outline: "none",
                    border: "none",
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    handleImageClick(
                      `https://sirius.runasp.net/${post.photos[0].filePath}`
                    )
                  }
                />
              )
            )}
            <CardContent>
              <Typography variant="h3" component="h2" fontWeight="bold">
                {post.title}
              </Typography>
              <Typography
                variant="caption"
                color="textSecondary"
                display="block"
                mt={1}
                style={{fontSize: "1rem", display:'flex', gap:'5px'}}
              >
                {post.publishTime ? (
                  <p>{timeAgo(post.publishTime)}</p>
                ) : (
                  "Unknown Date"
                )}
                
                <span
                  style={{ fontSize: "1rem", opacity: 1, color: "#e84545" }}
                >
                  Author - {post.userName || "Unknown Author"}
                </span>
              </Typography>
              <Box 
                mt={2} 
                sx={{ 
                  padding: "1rem 4rem",
                  "& .ql-indent-1": { paddingLeft: "3em !important" },
                  "& .ql-indent-2": { paddingLeft: "6em !important" },
                  "& ul, ol": { 
                    paddingLeft: "2em !important",
                    margin: "1em 0 !important" 
                  },
                  "& li": { 
                    marginBottom: "0.5em !important",
                    listStyleType: "disc" 
                  },
                  "& p": {
                    marginBottom: "1em",
                    lineHeight: "1.6"
                  }
                }}
              >
                {parse(cleanHtml)}
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>
      {modalOpen && (
        <ImageModal
          src={modalImage}
          alt="Selected Image"
          onClose={handleCloseModal}
        />
      )}
      <Footer />
    </div>
  );
};

export default PostDetail;
